import PropTypes from 'prop-types';
import { Link, useLocation, matchPath } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import IconButton from 'components/@extended/IconButton';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

export default function NavItem({ item, level, isParents = false, setSelectedID, subItem = false }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { pathname } = useLocation();
  const isSelected = !!matchPath({ path: item.url, end: false }, pathname);

  const itemHandler = () => {
    if (downLG) handlerDrawerOpen(false);
    if (isParents && setSelectedID) setSelectedID(item.id);
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1rem' : '1.25rem',
        ...(isParents && { fontSize: 20, stroke: '1.5' })
      }}
    />
  ) : null;

  return (
    <Box sx={{ position: 'relative' }}>
      <ListItemButton
        component={Link}
        to={item.url}
        selected={isSelected}
        onClick={itemHandler}
        sx={{
          position: 'relative',
          zIndex: 1201,
          pl: drawerOpen ? `${level * 28}px` : 1.5,
          py: !drawerOpen && level === 1 ? 1.25 : 1,

          /* ================= SUB ITEM DOT + LINE ================= */
          ...(subItem && {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: drawerOpen ? 12 : 6,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: isSelected ? '#3f7fc7ff' : '#6B7280',
              zIndex: 2
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: drawerOpen ? 14 : 8,
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: '#25324A',
              zIndex: 1
            }
          }),

          /* ================= PARENT ITEM STYLES ================= */
          ...(!subItem && {
            '&:hover': {
              bgcolor: '#25324A',
              '& .MuiTypography-root': { color: '#3B8FEE' },
              '& .MuiListItemIcon-root': { color: '#3B8FEE' }
            },
            '&.Mui-selected': {
              bgcolor: '#25324A',
              '& .MuiTypography-root': { color: '#3B8FEE' },
              '& .MuiListItemIcon-root': { color: '#3B8FEE' },
              '&:hover': { bgcolor: '#25324A' }
            }
          }),

          /* ================= SUB ITEM STYLES ================= */
          ...(subItem && {
            '&:not(.Mui-selected):hover': {
              bgcolor: '#25324A',
              '& .MuiTypography-root': { color: '#3B8FEE' },
              '& .MuiListItemIcon-root': { color: '#3B8FEE' }
            },
            '&.Mui-selected': {
              bgcolor: '#25324A',
              '& .MuiTypography-root': { color: '#3B8FEE' },
              '& .MuiListItemIcon-root': { color: '#3B8FEE' },
              '&:hover': { bgcolor: '#25324A' }
            }
          })
        }}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              width: 28,
              paddingLeft:drawerOpen?0:1,
              color: isSelected ? '#3B8FEE' : 'white'
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}

        {(drawerOpen || level !== 1) && (
          <ListItemText
            primary={
              <Typography variant="h6" sx={{ color: isSelected ? '#3B8FEE' : 'white' }}>
                {item.title}
              </Typography>
            }
          />
        )}

        {(drawerOpen || level !== 1) && item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size={item.chip.size}
            label={item.chip.label}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          />
        )}
      </ListItemButton>

      {(drawerOpen || level !== 1) &&
        item?.actions?.map((action, index) => {
          const ActionIcon = action.icon;
          return (
            <IconButton
              key={index}
              component={action.type === 'link' ? Link : 'button'}
              to={action.url}
              onClick={(e) => {
                e.stopPropagation();
                action?.function?.();
              }}
              sx={{
                position: 'absolute',
                top: 12,
                right: 20,
                width: 20,
                height: 20,
                color: 'secondary.dark'
              }}
            >
              <ActionIcon style={{ fontSize: '0.625rem' }} />
            </IconButton>
          );
        })}
    </Box>
  );
}

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  setSelectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.func]),
  subItem: PropTypes.bool
};
